/**
 * Font loading composable.
 *
 * Handles:
 * - Loading font catalog from server
 * - Loading Google Fonts via <link>
 * - Loading custom fonts via @font-face
 * - Tracking which fonts are already loaded
 *
 * Based on print.js designer.loadFonts/changeFont logic.
 */
import { ref } from 'vue';
import type { Pinia } from 'pinia';
import { useConfigStore } from '../stores/configStore';
import { requestDesignerJson } from '../services/designerRequest';
import { veyopAjaxUrl } from '@core/veyopRuntime';

export interface FontItem {
  id: string;
  title: string;
  type: 'google' | '' | string; // 'google' = Google Fonts, '' = legacy rows, also treated as Google Fonts
  filename?: string;
  url?: string;
  path?: string;
  thumb?: string;
  published?: number;
  categoryId?: string;
  /** Absolute URL to the font file, resolved server-side (custom fonts only). */
  font_preview_url?: string;
}

interface FontCatalogResponse {
  status?: number;
  fonts?: {
    google_fonts?: unknown;
    fonts?: Record<string, unknown>[];
    categories?: Record<string, unknown>[];
    cateFonts?: Record<string, { fonts?: Record<string, unknown>[] }>;
  };
}

export interface FontCategory {
  id: string;
  title: string;
}

/** `type === ''` is a legacy value that also means Google Font (custom uploads always have a `filename`). */
function isGoogleFontType(type: string): boolean {
  return type === 'google' || type === '';
}

const DEFAULT_SCOPE = Symbol('veyop-fonts-default');
const _instances = new Map<Pinia | typeof DEFAULT_SCOPE, ReturnType<typeof createFonts>>();

/**
 * Shared font engine — catalog is fetched once per session no matter how many components
 * use it.
 *
 * One instance per Pinia: font URLs come from that store's `veyopUrl`, so a headless
 * render on its own Pinia (template mockups) must not reuse the page-level instance,
 * whose config store may never have been initialised.
 */
export function useFonts(pinia?: Pinia | null) {
  const scope = pinia ?? DEFAULT_SCOPE;
  let instance = _instances.get(scope);
  if (!instance) {
    instance = createFonts(pinia ?? undefined);
    _instances.set(scope, instance);
  }
  return instance;
}

function createFonts(pinia?: Pinia) {
  const config = useConfigStore(pinia);

  const fonts = ref<FontItem[]>([]);
  const categories = ref<FontCategory[]>([]);
  const categoryFonts = ref<Record<string, FontItem[]>>({});
  const loaded = ref(false);
  const loadedFontIds = ref<Set<string>>(new Set());
  /** css strings injected into <head> (for custom @font-face usage) */
  const fontCssSnippets = ref<string[]>([]);
  /**
   * Google Fonts spec indexed by normalized family name (lowercase, spaces→+).
   * e.g. "Open Sans:400,700" stored as key "open+sans".
   */
  const googleFamilySpecs = new Map<string, string>();
  const injectedGoogleLinks = new Set<string>();
  /** Raw pipe-delimited Google Fonts spec string from the catalog response, for consumers that need to re-derive the family index (e.g. shared font-embedding resolution). */
  const googleFontSpecsRaw = ref('');

  /**
   * Load the font catalog from the server.
   */
  async function loadFontCatalog(): Promise<void> {
    if (loaded.value) return;
    try {
      const data = await requestDesignerJson<FontCatalogResponse>(veyopAjaxUrl('fonts', {}, config.data.veyopUrl), {
        operation: 'loadFonts',
        method: 'POST',
        readOnly: true,
      });
      if (data.status === 1 && data.fonts) {
        // Build Google Fonts lookup for lazy per-font injection (P2)
        if (data.fonts.google_fonts) {
          googleFontSpecsRaw.value = String(data.fonts.google_fonts);
          buildGoogleFamilyIndex(String(data.fonts.google_fonts));
        }
        // Process font list
        if (Array.isArray(data.fonts.fonts)) {
          fonts.value = data.fonts.fonts.map((f: Record<string, unknown>) => ({
            id: String(f.id ?? ''),
            title: String(f.title ?? ''),
            type: String(f.type ?? ''),
            filename: f.filename ? String(f.filename) : undefined,
            path: f.path ? String(f.path) : undefined,
            thumb: f.thumb ? String(f.thumb) : undefined,
            published: Number(f.published ?? 1),
            font_preview_url: f.font_preview_url ? String(f.font_preview_url) : undefined,
          }));
        }
        // Categories
        if (Array.isArray(data.fonts.categories)) {
          categories.value = data.fonts.categories.map((c: Record<string, unknown>) => ({
            id: String(c.id ?? ''),
            title: String(c.title ?? ''),
          }));
        }
        // Category → fonts mapping
        if (data.fonts.cateFonts) {
          for (const [catId, catData] of Object.entries(data.fonts.cateFonts)) {
            const cd = catData as Record<string, unknown>;
            if (Array.isArray(cd.fonts)) {
              categoryFonts.value[catId] = cd.fonts
                .filter((f: Record<string, unknown>) => Number(f.published ?? 1) !== 0)
                .map((f: Record<string, unknown>) => ({
                  id: String(f.id ?? ''),
                  title: String(f.title ?? ''),
                  type: String(f.type ?? ''),
                  filename: f.filename ? String(f.filename) : undefined,
                  path: f.path ? String(f.path) : undefined,
                  thumb: f.thumb ? String(f.thumb) : undefined,
                  published: Number(f.published ?? 1),
                  font_preview_url: f.font_preview_url ? String(f.font_preview_url) : undefined,
                }));
            }
          }
        }
        loaded.value = true;
      }
    } catch (e) {
      console.error('Failed to load fonts', e);
    }
  }

  /**
   * Build and inject @font-face CSS for a custom font (woff/woff2/ttf/otf).
   * `filename` is JSON: either {"woff":"file.woff"} or ["file.woff2", ...].
   */
  function injectCustomFontFace(font: FontItem): void {
    if (!font.filename) return;
    try {
      const fileData = JSON.parse(font.filename) as unknown;
      const files = Array.isArray(fileData)
        ? fileData.map(String)
        : typeof fileData === 'object' && fileData !== null
          ? Object.values(fileData as Record<string, unknown>).map(String)
          : [];
      const file = files.find((f) => /\.(woff2?|ttf|otf)$/i.test(f));
      if (file) {
        const format = file.toLowerCase().endsWith('.woff2')
          ? 'woff2'
          : file.toLowerCase().endsWith('.woff')
            ? 'woff'
            : file.toLowerCase().endsWith('.otf')
              ? 'opentype'
              : 'truetype';
        // font_preview_url is resolved server-side against the WordPress uploads
        // URL; veyopUrl (the plugin's own directory) is wrong for uploaded files.
        const fontUrl = font.font_preview_url || font.url || (config.data.veyopUrl + (font.path || '').replace(/\\/g, '/') + '/');
        const src = font.font_preview_url ? fontUrl : `${fontUrl}${file}`;
        const css = `@font-face{font-family:'${font.title}';font-style:normal;font-weight:400;src:local('${font.title}'),url(${src}) format('${format}');}`;
        const style = document.createElement('style');
        style.textContent = css;
        document.head.appendChild(style);
        fontCssSnippets.value.push(css);
      }
    } catch {
      // Filename wasn't valid JSON
    }
  }

  /**
   * Activate a specific font, loading it if necessary.
   * Returns the CSS font-family name.
   */
  async function activateFont(font: FontItem): Promise<string> {
    if (loadedFontIds.value.has(font.id)) {
      return font.title;
    }

    if (isGoogleFontType(font.type)) {
      await injectGoogleFontLazy(font.title);
      loadedFontIds.value.add(font.id);
      await ensureFontFaceReady(font.title);
      return font.title;
    }

    if (font.filename) {
      injectCustomFontFace(font);
      loadedFontIds.value.add(font.id);
      await ensureFontFaceReady(font.title);
    }

    return font.title;
  }

  /**
   * Activate every distinct font used by `text` items in a loaded design
   * (e.g. after loading a saved/cart design), so the editor renders with
   * the correct fonts without requiring the user to reopen the font picker.
   */
  async function activateFontsForItems(
    items: Array<{ type?: string; fontFamily?: unknown }>,
  ): Promise<void> {
    await loadFontCatalog();
    const families = new Set<string>();
    for (const item of items) {
      if (item?.type !== 'text') continue;
      const family = String(item.fontFamily ?? '').trim();
      if (family) families.add(family.toLowerCase());
    }
    if (families.size === 0) return;

    const matches = fonts.value.filter((f) => families.has(f.title.trim().toLowerCase()));
    await Promise.all(matches.map((font) => activateFont(font)));
  }

  /**
   * Wait until `family` is actually usable for rendering (not just requested),
   * so canvas/DOM rendering right after activation doesn't race the font
   * download. Never throws — degrades to default-font rendering on failure.
   */
  async function ensureFontFaceReady(family: string): Promise<void> {
    if (typeof document === 'undefined' || !document.fonts) return;
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, 4000));
    try {
      await Promise.race([
        Promise.all([
          document.fonts.load(`400 16px "${family}"`),
          document.fonts.load(`700 16px "${family}"`),
          document.fonts.load(`italic 16px "${family}"`),
        ]),
        timeout,
      ]);
    } catch {
      // Font failed to load/parse — leave rendering to fall back silently.
    }
  }

  /**
   * Preload font previews for a list of fonts (e.g. the currently visible
   * rows in a font picker), so their preview text renders in-font without
   * requiring the user to select them first. Google Fonts requests are
   * batched into a single pipe-delimited request per chunk.
   */
  function preloadFontPreviews(list: FontItem[]): void {
    const googleSpecs: string[] = [];

    for (const font of list) {
      if (loadedFontIds.value.has(font.id)) continue;

      if (isGoogleFontType(font.type)) {
        const key = font.title.trim().toLowerCase().replace(/\s+/g, '+');
        if (!injectedGoogleLinks.has(key)) {
          const spec = googleFamilySpecs.get(key) ?? font.title.trim().replace(/\s+/g, '+');
          googleSpecs.push(spec);
          injectedGoogleLinks.add(key);
        }
        loadedFontIds.value.add(font.id);
      } else if (font.filename) {
        injectCustomFontFace(font);
        loadedFontIds.value.add(font.id);
      }
    }

    const CHUNK_SIZE = 40;
    for (let i = 0; i < googleSpecs.length; i += CHUNK_SIZE) {
      const chunk = googleSpecs.slice(i, i + CHUNK_SIZE);
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css?family=${chunk.map((spec) => encodeURI(spec)).join('|')}`;
      document.head.appendChild(link);
    }
  }

  /**
   * Get fonts filtered by category. Pass '' or '0' for all.
   */
  function getFontsByCategory(categoryId: string): FontItem[] {
    if (!categoryId || categoryId === '0') {
      return fonts.value.filter((f) => f.published !== 0);
    }
    return categoryFonts.value[categoryId] ?? [];
  }

  /**
   * Get all accumulated @font-face CSS (for saving with design).
   */
  function getAllFontCss(): string {
    return fontCssSnippets.value.map((c) => `<style type="text/css">${c}</style>`).join(' ');
  }

  function buildGoogleFamilyIndex(googleFontsStr: string) {
    for (const spec of googleFontsStr.split('|').filter(Boolean)) {
      const familyPart = spec.split(':')[0];
      const key = familyPart.trim().toLowerCase().replace(/\s+/g, '+');
      if (key) googleFamilySpecs.set(key, spec.trim());
    }
  }

  function injectGoogleFontLazy(familyTitle: string): Promise<void> {
    const key = familyTitle.trim().toLowerCase().replace(/\s+/g, '+');
    if (injectedGoogleLinks.has(key)) return Promise.resolve();
    const spec = googleFamilySpecs.get(key) ?? familyTitle.trim().replace(/\s+/g, '+');
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css?family=${encodeURI(spec)}`;
    const ready = new Promise<void>((resolve) => {
      const settle = () => resolve();
      link.addEventListener('load', settle, { once: true });
      link.addEventListener('error', settle, { once: true });
      setTimeout(settle, 4000);
    });
    document.head.appendChild(link);
    injectedGoogleLinks.add(key);
    return ready;
  }

  return {
    fonts,
    categories,
    loaded,
    loadFontCatalog,
    activateFont,
    activateFontsForItems,
    preloadFontPreviews,
    getFontsByCategory,
    getAllFontCss,
    fontCssSnippets,
    googleFontSpecsRaw,
  };
}
